export type RawJson = Readonly<Record<string, unknown>>;

const ID_KEYS = ['id', 'uuid', 'nid', 'tid'] as const;
const UPDATED_KEYS = ['updated', 'changed', 'updatedAt', 'updated_at'] as const;
const COLLECTION_KEYS = ['data', 'items', 'results'] as const;

export function isRecord(value: unknown): value is RawJson {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function asRecord(value: unknown): RawJson {
  return isRecord(value) ? value : {};
}

export function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

export function readRecord(source: RawJson, ...keys: readonly string[]): RawJson | null {
  for (const key of keys) {
    const value = source[key];
    if (isRecord(value)) {
      return value;
    }
  }
  return null;
}

export function readString(source: RawJson, ...keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
  }
  return null;
}

export function readLocalized(source: RawJson, ...keys: readonly string[]): string | null {
  for (const key of keys) {
    const direct = readString(source, key);
    if (direct !== null) {
      return direct;
    }

    const translations = readRecord(source, key);
    if (translations === null) {
      continue;
    }

    for (const code of Object.keys(translations)) {
      const translation = readString(translations, code);
      if (translation !== null) {
        return translation;
      }
    }
  }
  return null;
}

export function readNumber(source: RawJson, ...keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

export function readBoolean(source: RawJson, ...keys: readonly string[]): boolean | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') {
      return value;
    }
    if (value === 'true' || value === 1 || value === '1') {
      return true;
    }
    if (value === 'false' || value === 0 || value === '0') {
      return false;
    }
  }
  return null;
}

export function readArray(source: RawJson, ...keys: readonly string[]): readonly unknown[] {
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

export function readDate(source: RawJson, ...keys: readonly string[]): Date | null {
  const raw = readString(source, ...keys);
  if (raw === null) {
    return null;
  }
  const asTimestamp = Number(raw);
  const date = Number.isFinite(asTimestamp) ? new Date(asTimestamp * 1000) : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function readId(source: RawJson): string | null {
  return readString(source, ...ID_KEYS);
}

export function readUpdatedAt(source: RawJson): Date | null {
  return readDate(source, ...UPDATED_KEYS);
}

export function unwrapCollection(payload: unknown, ...keys: readonly string[]): readonly unknown[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  const record = asRecord(payload);
  for (const key of [...COLLECTION_KEYS, ...keys]) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}
